import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api/client";

export type ReferrerPreview = {
  firstName: string;
  lastName: string | null;
  companyName: string | null;
} | null;

type ReferrerResponse = { referrer: ReferrerPreview };

/**
 * "Tony Mauro · Sukha", or just "Tony Mauro" when there's no company (and no
 * dangling separator). Language-neutral separator keeps the parts i18n-safe.
 */
export function referrerDisplayName(referrer: NonNullable<ReferrerPreview>): string {
  const fullName = [referrer.firstName, referrer.lastName]
    .filter(Boolean)
    .join(" ");
  return referrer.companyName && referrer.companyName !== fullName
    ? `${fullName} · ${referrer.companyName}`
    : fullName;
}

/** Resolves a landing ?ref= code to a display-safe partner name (public). */
export function useReferrerPreview(refCode: string | null) {
  return useQuery({
    queryKey: ["tolt-referrer-preview", refCode],
    queryFn: () =>
      apiGet<ReferrerResponse>(
        `/api/v1/tolt/partner-preview?ref=${encodeURIComponent(refCode ?? "")}`
      ),
    enabled: Boolean(refCode),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

/** Who referred the signed-in member (onboarding greeting). */
export function useMyReferrer(enabled = true) {
  return useQuery({
    queryKey: ["tolt-my-referrer"],
    queryFn: () => apiGet<ReferrerResponse>("/api/v1/tolt/my-referrer"),
    enabled,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}
